export type Tool = {
  slug: string;
  name: string;
  shortName: string; // Name without "Advanced Search" or "Generator" etc. (e.g., "X" instead of "X Advanced Search")
  description: string;
  category: "Search" | "Developer" | "Utility";
  displayOrder: number;
  icon: string; // Icon name for DynamicIcon (e.g., "FaXTwitter")
  badge?: string; // Optional badge text (e.g., "Popular")
};

// Centralized list of all free tools
const TOOLS: Tool[] = [
  {
    slug: "x-advanced-search",
    name: "X Advanced Search",
    shortName: "X",
    description:
      "Build complex X (Twitter) search queries without memorizing operators. Filter by dates, engagement, accounts, hashtags, language, and more.",
    category: "Search",
    displayOrder: 1,
    icon: "FaXTwitter",
    badge: "Popular",
  },
  {
    slug: "google-advanced-search",
    name: "Google Advanced Search",
    shortName: "Google",
    description:
      "Master Google search with a visual interface. Filter by file type, domain, exact matches, numeric ranges, language, region, and more.",
    category: "Search",
    displayOrder: 2,
    icon: "FaGoogle",
  },
  {
    slug: "reddit-advanced-search",
    name: "Reddit Search Tool",
    shortName: "Reddit Search",
    description:
      "Build advanced Reddit search queries without memorizing operators. Search by author, subreddit, site domain, title, self-posts, date, NSFW filtering, and more.",
    category: "Search",
    displayOrder: 3,
    icon: "FaReddit",
  },
  {
    slug: "youtube-advanced-search",
    name: "YouTube Advanced Search",
    shortName: "YouTube",
    description:
      "Find specific video clips for research with advanced filters. Filter by upload date, duration, video type, quality (HD, 4K), creative commons, and more.",
    category: "Search",
    displayOrder: 4,
    icon: "FaYoutube",
  },
  {
    slug: "manifest-generator",
    name: "Chrome Extension Manifest V3 Generator",
    shortName: "Manifest Generator",
    description:
      "Generate a compliant Chrome Extension Manifest V3 file instantly. Configure permissions, background service workers, action popups, content scripts, and more.",
    category: "Developer",
    displayOrder: 5,
    icon: "FaChrome",
  },
  {
    slug: "video-bitrate-calculator",
    name: "Video Bitrate & File Size Calculator",
    shortName: "Bitrate Calculator",
    description:
      "Calculate optimal video bitrates and estimated file sizes for Discord, Email, or Streaming. Perfect for staying under upload limits.",
    category: "Utility",
    displayOrder: 6,
    icon: "HiCalculator",
  },
  {
    slug: "chrome-extension-icon-resizer",
    name: "Chrome Extension Icon Resizer",
    shortName: "Icon Resizer",
    description:
      "Generate all required icon sizes for your Chrome Extension from a single image. Automatically creates 16x16, 32x32, 48x48, and 128x128 icons.",
    category: "Developer",
    displayOrder: 7,
    icon: "HiPhotograph",
  },
  {
    slug: "chrome-extension-promo-tile-generator",
    name: "Chrome Extension Promo Tile Generator",
    shortName: "Promo Tile Generator",
    description:
      "Generate all 3 Chrome Web Store promo tile sizes from a single image: Small (440x280), Large (920x680), and Marquee (1400x560). Processes in your browser.",
    category: "Developer",
    displayOrder: 8,
    icon: "HiPhotograph",
  },
  {
    slug: "chrome-extension-screenshot-resizer",
    name: "Chrome Extension Screenshot Resizer",
    shortName: "Screenshot Resizer",
    description:
      "Resize any image to Chrome Web Store screenshot specs (640x400 or 1280x800 — the only two accepted sizes). Processes in your browser, no upload.",
    category: "Developer",
    displayOrder: 9,
    icon: "HiPhotograph",
  },
  {
    slug: "favicon-multi-format-generator",
    name: "Favicon Multi Format Generator",
    shortName: "Favicon Generator",
    description:
      "Generate favicons in all required formats and sizes from a single image. Creates ICO, PNG formats (16x16, 32x32, 180x180, 192x192, 512x512) for websites, mobile apps, and PWA support.",
    category: "Developer",
    displayOrder: 8,
    icon: "HiPhotograph",
  },
  {
    slug: "twitter-thread-formatter",
    name: "Twitter Thread Formatter",
    shortName: "Thread Formatter",
    description:
      "Format long text into perfectly-sized Twitter/X thread posts with character counts. Automatically splits at sentence boundaries and adds numbering.",
    category: "Utility",
    displayOrder: 9,
    icon: "FaXTwitter",
  },
  {
    slug: "instagram-hashtag-generator",
    name: "Instagram Hashtag Generator",
    shortName: "Hashtag Generator",
    description:
      "Generate relevant hashtag sets for Instagram posts by niche and content type. Mix of popular, medium, and niche hashtags for maximum reach.",
    category: "Utility",
    displayOrder: 10,
    icon: "FaInstagram",
    badge: "New",
  },
  {
    slug: "social-media-image-resizer",
    name: "Social Media Image Resizer",
    shortName: "Image Resizer",
    description:
      "Resize and crop images for every social media platform. Presets for Instagram, Twitter, Facebook, LinkedIn, YouTube, and TikTok covers and posts.",
    category: "Utility",
    displayOrder: 11,
    icon: "HiPhotograph",
  },
  {
    slug: "twitter-bio-generator",
    name: "Twitter Bio Generator",
    shortName: "Bio Generator",
    description:
      "Build a compelling Twitter/X bio using proven templates. Choose from professional, creative, founder, and influencer styles with character count.",
    category: "Utility",
    displayOrder: 12,
    icon: "FaXTwitter",
  },
  {
    slug: "reddit-title-analyzer",
    name: "Reddit Title Analyzer",
    shortName: "Title Analyzer",
    description:
      "Score your Reddit post titles for engagement potential. Analyzes length, emotional triggers, question format, and subreddit-specific patterns.",
    category: "Utility",
    displayOrder: 13,
    icon: "FaReddit",
  },
  {
    slug: "email-subject-line-tester",
    name: "Email Subject Line Tester",
    shortName: "Subject Line Tester",
    description:
      "Preview and score email subject lines before sending. Check character length, spam triggers, emoji rendering, and mobile preview across clients.",
    category: "Utility",
    displayOrder: 14,
    icon: "HiMail",
  },
  {
    slug: "chrome-extension-size-checker",
    name: "Chrome Extension Size Checker",
    shortName: "Size Checker",
    description:
      "Check if your Chrome extension ZIP meets Chrome Web Store size limits. Analyzes file breakdown, identifies large assets, and suggests optimizations.",
    category: "Developer",
    displayOrder: 15,
    icon: "FaChrome",
  },
  {
    slug: "json-to-csv-converter",
    name: "JSON to CSV Converter",
    shortName: "JSON to CSV",
    description:
      "Convert JSON data to CSV format instantly. Handles nested objects, arrays, and complex structures. Paste JSON or upload a file to get a downloadable CSV.",
    category: "Utility",
    displayOrder: 16,
    icon: "HiDatabase",
    badge: "New",
  },
  {
    slug: "ai-prompt-builder",
    name: "AI Prompt Builder",
    shortName: "Prompt Builder",
    description:
      "Build structured AI prompts with a visual editor. Add role, context, task, format, and constraints. Export ready-to-paste prompts for ChatGPT, Claude, or any AI.",
    category: "Utility",
    displayOrder: 17,
    icon: "HiLightningBolt",
    badge: "New",
  },
  {
    slug: "video-thumbnail-generator",
    name: "Video Thumbnail Text Generator",
    shortName: "Thumbnail Generator",
    description:
      "Generate eye-catching YouTube thumbnail text layouts. Preview different font styles, colors, and overlays on your uploaded image. Download as PNG.",
    category: "Utility",
    displayOrder: 18,
    icon: "FaYoutube",
  },
  {
    slug: "youtube-thumbnail-downloader",
    name: "YouTube Thumbnail Downloader",
    shortName: "Thumbnail Downloader",
    description:
      "Paste any YouTube URL to download its thumbnail in every available quality — HD (1280×720), SD, HQ, MQ. Works with Shorts and embed URLs.",
    category: "Utility",
    displayOrder: 19,
    icon: "FaYoutube",
    badge: "New",
  },
  {
    slug: "tweet-character-counter",
    name: "Tweet Character Counter",
    shortName: "Tweet Counter",
    description:
      "Live character counter for X/Twitter posts. Tracks the 280 free limit and 25,000 Premium limit, weights emojis and CJK characters like X does, and previews thread splits.",
    category: "Utility",
    displayOrder: 20,
    icon: "FaXTwitter",
    badge: "New",
  },
  {
    slug: "youtube-tag-generator",
    name: "YouTube Tag Generator",
    shortName: "Tag Generator",
    description:
      "Generate 30+ relevant YouTube tags from your video topic or title. Stays under YouTube's 500-character tags limit. Copy individually or all at once.",
    category: "Utility",
    displayOrder: 21,
    icon: "FaYoutube",
    badge: "New",
  },
  {
    slug: "tiktok-caption-generator",
    name: "TikTok Caption Generator",
    shortName: "Caption Generator",
    description:
      "Generate TikTok captions with proven hooks, CTAs, and trending hashtags by niche. Stays under TikTok's 2,200-character limit. Copy or remix instantly.",
    category: "Utility",
    displayOrder: 22,
    icon: "HiVideoCamera",
    badge: "New",
  },
  {
    slug: "instagram-engagement-rate-calculator",
    name: "Instagram Engagement Rate Calculator",
    shortName: "IG Engagement Rate",
    description:
      "Calculate your Instagram engagement rate by followers or reach, including saves and shares, and compare it against benchmarks for your account size.",
    category: "Utility",
    displayOrder: 23,
    icon: "HiCalculator",
    badge: "New",
  },
];

export function getAllTools(): Tool[] {
  return TOOLS.sort((a, b) => a.displayOrder - b.displayOrder);
}

export function getToolBySlug(slug: string): Tool | null {
  return TOOLS.find((tool) => tool.slug === slug) || null;
}
